import React from "react";
import { message, Modal } from "antd";
import { Space } from "lib";
import { navigate } from "gatsby";
import { QuestionType, FileResponse } from "@api";
import moment from "moment";
import { login, mgr } from "@services/auth";

export const showError = (error: any): void => {
  console.log(error);
  try {
    switch (error.status) {
      case 401:
        Modal.info({
          title: "閒置過久,連線階段已過期,請重新登入",
          centered: true,
          onOk: () =>
            mgr
              ?.signinSilent()
              .then(() => window.location.reload())
              .catch(() => login()),
        });
        return;
      case 404:
        message.warning("找不到相關紀錄");
        return;
      case 403:
        message.warning("沒有相關權限");
        return;
      case 500:
        message.error("系統發生錯誤,請稍後再試");
        return;
      case 503:
        message.warning("系統正在進行維護,請稍後再試");
        return;
      default:
        break;
    }
    if (!error?.response) return;
    const response = JSON.parse(error.response);
    const errList = response.errors || {};

    // message.warning(
    //     <Space>
    //         {Object.values(errList).map((msg, i) => `${i + 1} :${msg}`)}
    //     </Space>,
    // );

    const errorMessages = Object.values(errList)
      .flatMap((msg: any) => (Array.isArray(msg) ? msg : [msg]))
      .flatMap((msg: any) => String(msg).split(/\r?\n/))
      .map((msg) => msg.trim())
      .filter(Boolean);

    message.warning({
      content: (
        <Space direction="vertical" size={0}>
          {errorMessages.map((msg, index) => (
            <div key={index} style={{ whiteSpace: "pre-line" }}>
              {msg}
            </div>
          ))}
        </Space>
      ),
    });
  } catch (err) {
    message.error("系統發生錯誤");
  }
};

export const handlePageError = (error: any): void => {
  console.log(error);
  try {
    switch (error.status) {
      case 404:
        navigate("/404/", { replace: true });
        return;
      case 403:
        navigate("/403/", { replace: true });
        return;
      default:
        message.error("系統發生錯誤");
        window.history.back();
        break;
    }
  } catch (err) {
    message.error("系統發生錯誤");
  }
};

export const findAnswer: any = (type: QuestionType, answers: any) => {
  switch (type) {
    case QuestionType.Text:
      return answers?.textAnswer;
    case QuestionType.SingleRadio:
    case QuestionType.SingleDropDown:
      return answers?.questionChoiceId;
    case QuestionType.Scale:
      return answers?.ratingScalePointId;
    default:
      return null;
  }
};

export const translateAnswer: any = (type: QuestionType, value: any) => {
  switch (type) {
    case QuestionType.Text:
      return { textAnswer: value };
    case QuestionType.SingleRadio:
    case QuestionType.SingleDropDown:
      return { questionChoiceId: value };
    case QuestionType.Scale:
      return { ratingScalePointId: value };
    default:
      return null;
  }
};

export const formatDate = (
  date: string | Date | moment.Moment,
  format = process.env.DATE_FORMAT,
) => (date ? moment(date).format(format) : "-");

export const Download = (f: FileResponse) => {
  const url = URL.createObjectURL(new Blob([f.data]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", f.fileName ?? "");
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export function isWithinTimeWindow(
  startDate?: Date,
  endDate?: Date,
  durationDays?: number,
  firstClickDate?: Date,
): boolean {
  const now = new Date();
  console.log("laile ");

  // ========== 开始时间校验 ==========
  // 有 startDate 时：当前时间必须 >= startDate
  if (startDate && now < startDate) {
    return false;
  }

  // ========== 结束时间校验（优先级：endDate > durationDays）==========
  let endBoundary: Date | null = null;

  // 情况1: endDate 存在（最高优先级，忽略 durationDays）
  if (endDate) {
    endBoundary = endDate;
  }
  // 情况2: 无 endDate，但有 startDate + 有效 durationDays
  else if (
    startDate &&
    durationDays !== undefined &&
    Number.isFinite(durationDays) &&
    durationDays >= 0
  ) {
    // 严格对齐 formatTimeWindow 的计算逻辑：startDate + durationDays * 24h
    endBoundary = new Date(startDate.getTime() + durationDays * 86_400_000);
  }
  // 情况2b: 无 endDate 且无 startDate，但有 firstClickDate + 有效 durationDays
  else if (
    !startDate &&
    firstClickDate &&
    durationDays !== undefined &&
    Number.isFinite(durationDays) &&
    durationDays >= 0
  ) {
    // 以 firstClickDate 的本地日为起点（去除时分秒），确保按“整天”计算。
    const startOfFirstClickDay = new Date(
      firstClickDate.getFullYear(),
      firstClickDate.getMonth(),
      firstClickDate.getDate(),
    );
    // 作为边界使用：起始日的 00:00 + (durationDays + 1) 天 - 1 毫秒，保证覆盖最后一天的整天时间
    const msPerDay = 86_400_000;
    endBoundary = new Date(
      startOfFirstClickDay.getTime() + (durationDays + 1) * msPerDay - 1,
    );
  }
  // 情况3: 无 endDate 且无有效结束条件 → 无结束限制（永久有效）
  //   - 仅有 startDate 无 durationDays
  //   - startDate/endDate 均为空（formatTimeWindow 返回 "-" 的场景）
  //   - 仅有 durationDays（无 startDate，按 formatTimeWindow 逻辑视为无限制）

  // 有结束边界时：当前时间必须 <= endBoundary
  if (endBoundary && now >= endBoundary) {
    return false;
  }

  // 通过所有校验：在窗口期内
  return true;
}

export function formatTimeWindow(
  startDate?: Date,
  endDate?: Date,
  durationDays?: number,
  firstClickDate?: Date,
): string {
  // Format as YYYY/MM/DD (local date, avoids UTC shift)
  const formatDate = (date: Date): string =>
    `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(2, "0")}/${String(date.getDate()).padStart(2, "0")}`;

  // Check if two dates represent the same calendar day (local time)
  const isSameLocalDay = (a: Date, b: Date): boolean =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  // Case 1: No start/end dates → neutral placeholder
  if (
    !startDate &&
    !endDate &&
    (durationDays === undefined || durationDays === null)
  ) {
    return "-"; // Intentional: "-" denotes "no time restriction" per your design
  }

  // Case 2: End date exists (highest priority)
  if (endDate) {
    const endStr = formatDate(endDate);
    if (startDate) {
      const startStr = formatDate(startDate);
      // Optimize same-day display
      if (isSameLocalDay(startDate, endDate))
        return `Only available on ${startStr};`;
      return `${startStr} – ${endStr}`; // En dash for professional typography
    }
    return `Until ${endStr}`;
  }

  // Case 2.5: No explicit startDate but we have firstClickDate + durationDays
  // (show a window derived from the user's first click)
  if (
    !startDate &&
    firstClickDate &&
    durationDays !== undefined &&
    durationDays !== null &&
    durationDays >= 0
  ) {
    const end = new Date(firstClickDate.getTime() + durationDays * 86_400_000);
    const startStr = formatDate(firstClickDate);
    const endStr = formatDate(end);
    if (startStr === endStr) return `Only available on ${startStr}`;
    return `${startStr} – ${endStr}`;
  }

  // Case 3: Start date exists, no end date
  if (startDate) {
    console.log("durationDays");
    console.log(durationDays);

    // Valid non-negative duration provided
    if (
      durationDays !== undefined &&
      durationDays !== null &&
      durationDays >= 0
    ) {
      const end = new Date(startDate.getTime() + durationDays * 86_400_000);
      const startStr = formatDate(startDate);
      const endStr = formatDate(end);
      if (startStr === endStr) return `Only available on ${startStr}`; // Single-day optimization
      return `${startStr} – ${endStr}`;
    }
    // Open-ended start date
    return `From ${formatDate(startDate)}`;
  }

  return "-"; // Fallback (theoretically unreachable)
}

export const DownloadUrl = (f: string, name: string) => {
  if (!f) {
    console.error("Download URL is empty");
    return;
  }
  const link = document.createElement("a");
  link.href = f;
  link.setAttribute("download", name);

  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
